import { useCallback, useMemo, useReducer } from 'react';
import type { components } from '@pocapan/shared/types/schema';

type ChatRoomUserPicture = components['schemas']['ApiV1BettieChatRoomUserPicture'] & {
  nickname?: string;
};

type State = {
  backupPictures: ChatRoomUserPicture[];
  originalDataUrlByPictureUrl: Record<string, string>;
};

type Action =
  | {
      type: 'ADD_LOCAL_UPLOAD';
      payload: { dataUrl: string; originalDataUrl: string };
    }
  | {
      type: 'ADD_BY_URL';
      payload: { pictureUrl: string; position: 'front' | 'back'; nickname?: string };
    }
  | {
      type: 'REMOVE_BY_URL';
      payload: { pictureUrl: string };
    }
  | {
      type: 'CLEAR_LOCAL_UPLOAD_MEMORY';
    };

const initialState: State = {
  backupPictures: [],
  originalDataUrlByPictureUrl: {}
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'ADD_LOCAL_UPLOAD': {
      const { dataUrl, originalDataUrl } = action.payload;
      if (state.backupPictures.some((p) => p.pictureUrl === dataUrl)) {
        return {
          ...state,
          originalDataUrlByPictureUrl: {
            ...state.originalDataUrlByPictureUrl,
            [dataUrl]: originalDataUrl
          }
        };
      }

      const nextPicture: ChatRoomUserPicture = {
        type: 'backup',
        pictureUrl: dataUrl,
        thumbnailUrl: null,
        originalPictureUrl: null,
        visibility: true
      };

      return {
        backupPictures: [nextPicture, ...state.backupPictures],
        originalDataUrlByPictureUrl: {
          ...state.originalDataUrlByPictureUrl,
          [dataUrl]: originalDataUrl
        }
      };
    }

    case 'ADD_BY_URL': {
      const { pictureUrl, position, nickname } = action.payload;
      const existsIndex = state.backupPictures.findIndex((p) => p.pictureUrl === pictureUrl);
      // 중복 URL이 들어오더라도(레이스/재추천 등) "최근 추가" 의미를 살려 front 요청이면 맨 앞으로 끌어올림
      if (existsIndex >= 0) {
        if (position !== 'front' && !nickname) return state;

        const existing = state.backupPictures[existsIndex];
        const updated: ChatRoomUserPicture = nickname
          ? { ...existing, nickname }
          : existing;

        if (position !== 'front') {
          const next = [...state.backupPictures];
          next[existsIndex] = updated;
          return { ...state, backupPictures: next };
        }

        const without = state.backupPictures.filter((p) => p.pictureUrl !== pictureUrl);
        return { ...state, backupPictures: [updated, ...without] };
      }

      const nextPicture: ChatRoomUserPicture = {
        type: 'backup',
        pictureUrl,
        thumbnailUrl: null,
        originalPictureUrl: null,
        visibility: true,
        nickname: nickname ?? undefined
      };

      return {
        ...state,
        backupPictures:
          position === 'front'
            ? [nextPicture, ...state.backupPictures]
            : [...state.backupPictures, nextPicture]
      };
    }

    case 'REMOVE_BY_URL': {
      const { pictureUrl } = action.payload;
      if (!state.backupPictures.some((p) => p.pictureUrl === pictureUrl)) return state;

      const { [pictureUrl]: _removed, ...restOriginal } = state.originalDataUrlByPictureUrl;
      return {
        backupPictures: state.backupPictures.filter((p) => p.pictureUrl !== pictureUrl),
        originalDataUrlByPictureUrl: restOriginal
      };
    }

    case 'CLEAR_LOCAL_UPLOAD_MEMORY': {
      const uploadUrls = new Set(Object.keys(state.originalDataUrlByPictureUrl));
      if (uploadUrls.size === 0) return state;

      return {
        backupPictures: state.backupPictures.filter((p) => !uploadUrls.has(p.pictureUrl)),
        originalDataUrlByPictureUrl: {}
      };
    }

    default:
      return state;
  }
};

export const useLocalBettieBackupPictures = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const addLocalUpload = useCallback((payload: { dataUrl: string; originalDataUrl: string }) => {
    dispatch({ type: 'ADD_LOCAL_UPLOAD', payload });
  }, []);

  const addBackupByUrl = useCallback(
    (payload: { pictureUrl: string; position?: 'front' | 'back'; nickname?: string }) => {
      dispatch({
        type: 'ADD_BY_URL',
        payload: {
          pictureUrl: payload.pictureUrl,
          position: payload.position ?? 'back',
          nickname: payload.nickname
        }
      });
    },
    []
  );

  const removeBackupByUrl = useCallback((pictureUrl: string) => {
    dispatch({ type: 'REMOVE_BY_URL', payload: { pictureUrl } });
  }, []);

  const clearLocalUploadMemory = useCallback(() => {
    dispatch({ type: 'CLEAR_LOCAL_UPLOAD_MEMORY' });
  }, []);

  const existingPictureUrls = useMemo(() => {
    return state.backupPictures.map((p) => p.pictureUrl);
  }, [state.backupPictures]);

  return {
    localBackupPictures: state.backupPictures,
    localOriginalDataUrlByPictureUrl: state.originalDataUrlByPictureUrl,
    existingPictureUrls,
    addLocalUpload,
    addBackupByUrl,
    removeBackupByUrl,
    clearLocalUploadMemory
  };
};
